import { FC, useMemo } from 'react'
import styles from 'components/features/pfm/TopSpendingCategoriesModule/TopSpendingCategoriesModule.styl'
import { Guards } from 'interfaces/guards'
import { peergroupsGuard } from 'guards/globalGuards'
import { createParallelGuard, connectShadedLoadable } from 'helpers/loadableHelpers'
import { memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard } from 'guards/moneyflowGuards'
import { MoneyflowBreakdownOwnProps, MoneyflowBreakdownCollection, MoneyflowBreakdownItem } from 'interfaces/moneyflow'
import { useShallowSelector, useStoreSelector } from 'helpers/reactReduxHelpers'
import { categoriesMoneyflowBreakdownSelector } from 'selectors/moneyflowSelectors'
import { CategoryBreakdown } from 'components/features/pfm/CategoryBreakdown/CategoryBreakdown'
import { getItemsByKey } from 'helpers/normalizeHelpers'
import { getMax } from 'helpers/formatHelpers'
import { metaCategoriesSelector } from 'selectors/globalSelectors'
import { selectedRelationSelector } from 'selectors/peergroupSelectors'
import PeergroupLegend from 'components/features/pfm/PeergroupLegend/PeergroupLegend'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'
import cn from 'classnames'

const getValue = (item: MoneyflowBreakdownItem) => item.projected || item.amount

const TOP_CATEGORIES_COUNT = 5
const sortCategories = (categories: MoneyflowBreakdownCollection) => {
  return [...Object.entries(categories.byKey)]
    .sort(([, a], [, b]) => {
      if (b.projected && a.projected) {
        return b.projected - a.projected
      } else {
        return b.amount - a.amount
      }
    })
    .slice(0, TOP_CATEGORIES_COUNT)
    .map(([id]) => id)
}

type TopSpendingCategoriesModuleProps =
  & MoneyflowBreakdownOwnProps
  & {

  }

const TopSpendingCategoriesComponent: FC<TopSpendingCategoriesModuleProps> = (props) => {
  const withComparison = useStoreSelector(withComparisonSelector)

  const [member, peers, other] = useShallowSelector((state) => {
    return categoriesMoneyflowBreakdownSelector(state, props) || [null, null, null]
  })

  const order = useMemo(() => {
    if (!member || !peers || !other) {
      return null
    }

    return sortCategories(member)
  }, [member, peers, other])

  const max = useMemo(() => {
    if (!order || !member || !peers || !other) {
      return null
    }

    return getMax([
      ...getItemsByKey(member.byKey, order).map(getValue),
      ...withComparison ? getItemsByKey(peers.byKey, order).map(getValue) : [],
      ...withComparison ? getItemsByKey(other.byKey, order).map(getValue) : [],
    ])
  }, [order, member, peers, other, withComparison])

  const categories = useStoreSelector((state) => metaCategoriesSelector(state, { type: 'spending' }))

  const listChildren = useMemo(() => {
    if (!order || !member || !peers || !other || max === null) {
      return null
    }

    return order.map((id) => (
      <div className={ styles.category } key={ id } data-id={ id }>
        <CategoryBreakdown
          key={ id }
          category={ categories.byKey[id] }
          member={ member.byKey[id] }
          peers={ peers.byKey[id] }
          other={ other.byKey[id] }
          maxTotal={ max }
          withComparison={ withComparison }
        />
      </div>
    ))
  }, [order, member, peers, other, max, categories.byKey, withComparison])

  const selectedRelation = useShallowSelector(selectedRelationSelector)

  return (
    <div className={ styles.topSpendingCategoriesModule }>
      { selectedRelation && (
        <div className={ cn(styles.legend, !withComparison && styles.hideLegend) }>
          <PeergroupLegend othersName={ selectedRelation.name } theme='top-spendings' />
        </div>
      )}
      { listChildren }
    </div>
  )
}

const guards: Guards<TopSpendingCategoriesModuleProps> = [
  peergroupsGuard,
  createParallelGuard(memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard),
]

export const TopSpendingCategoriesModule = connectShadedLoadable<
  {},
  {},
  MoneyflowBreakdownOwnProps,
  TopSpendingCategoriesModuleProps
>(() => () => ({}), undefined, guards)(TopSpendingCategoriesComponent)
